import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Updater } from '@tanstack/react-table';
import {
  getColumnSizingStorageKey,
  readColumnSizing,
  removeDefaultColumnSizing,
  resetAllColumnSizing,
  resetColumnSizing,
  writeColumnSizing,
  type ColumnSizingState,
} from 'utils/table/columnSizing.ts';

interface UseColumnSizingOptions {
  tableId: string;
  agendaId: string;
  viewId: string;
  queryKey: string;
  defaultColumnSize: number;
}

export const useColumnSizing = ({ tableId, agendaId, viewId, queryKey, defaultColumnSize }: UseColumnSizingOptions) => {
  const columnSizingStorageKey = useMemo(
    () => (tableId ? getColumnSizingStorageKey({ tableId, agendaId, viewId, queryKey }) : null),
    [agendaId, queryKey, tableId, viewId]
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    removeDefaultColumnSizing(readColumnSizing(columnSizingStorageKey), defaultColumnSize)
  );
  const columnSizingRef = useRef(columnSizing);
  const columnSizingWriteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleColumnSizingWrite = useCallback(
    (nextSizing: ColumnSizingState) => {
      if (columnSizingWriteTimeoutRef.current) {
        clearTimeout(columnSizingWriteTimeoutRef.current);
      }

      columnSizingWriteTimeoutRef.current = setTimeout(() => {
        writeColumnSizing(columnSizingStorageKey, nextSizing);
        columnSizingWriteTimeoutRef.current = null;
      }, 150);
    },
    [columnSizingStorageKey]
  );

  const updateColumnSizing = useCallback(
    (nextSizing: ColumnSizingState) => {
      const changedColumnSizing = removeDefaultColumnSizing(nextSizing, defaultColumnSize);
      columnSizingRef.current = changedColumnSizing;
      setColumnSizing(changedColumnSizing);
      scheduleColumnSizingWrite(changedColumnSizing);
    },
    [defaultColumnSize, scheduleColumnSizingWrite]
  );

  const handleColumnSizingChange = useCallback(
    (updaterOrValue: Updater<ColumnSizingState>) => {
      const previousSizing = columnSizingRef.current;
      const nextSizing = typeof updaterOrValue === 'function' ? updaterOrValue(previousSizing) : updaterOrValue;

      updateColumnSizing(nextSizing);
    },
    [updateColumnSizing]
  );

  const handleColumnSizingReset = useCallback(
    (columnId: string) => {
      updateColumnSizing(resetColumnSizing(columnSizingRef.current, columnId));
    },
    [updateColumnSizing]
  );

  const handleAllColumnSizingReset = useCallback(() => {
    updateColumnSizing(resetAllColumnSizing());
  }, [updateColumnSizing]);

  const hasChangedColumnSizing = useMemo(() => Object.keys(columnSizing).length > 0, [columnSizing]);

  useEffect(() => {
    const storedColumnSizing = readColumnSizing(columnSizingStorageKey);
    const changedColumnSizing = removeDefaultColumnSizing(storedColumnSizing, defaultColumnSize);
    columnSizingRef.current = changedColumnSizing;
    setColumnSizing(changedColumnSizing);

    if (Object.keys(storedColumnSizing).length !== Object.keys(changedColumnSizing).length) {
      writeColumnSizing(columnSizingStorageKey, changedColumnSizing);
    }
  }, [columnSizingStorageKey, defaultColumnSize]);

  useEffect(() => {
    return () => {
      if (columnSizingWriteTimeoutRef.current) {
        clearTimeout(columnSizingWriteTimeoutRef.current);
      }
      writeColumnSizing(columnSizingStorageKey, columnSizingRef.current);
    };
  }, [columnSizingStorageKey]);

  return {
    columnSizing,
    handleColumnSizingChange,
    handleColumnSizingReset,
    handleAllColumnSizingReset,
    hasChangedColumnSizing,
  };
};
